// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import { mergeClasses } from '@fluentui/react-components';
import { Add20Regular } from '@fluentui/react-icons';
import { ErrorCode } from '@microsoft/vscodeedu-api';
import React, { useCallback, useContext, useEffect } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { IntlProps, NotSignedInState, TrackContext, TrackedButton } from '../components/common';
import CreateActivityDialog from '../components/curriculum-list/create-activity-dialog';
import CurriculumList from '../components/curriculum-list/curriculum-list';
import DeleteActivityDialog from '../components/curriculum-list/delete-activity-dialog';
import LinkGithubDialog from '../components/curriculum-list/link-github-dialog';
import UnpublishActivityDialog from '../components/curriculum-list/unpublish-activity-dialog';
import PageContainer from '../components/page-container';
import { getIsSignedIn, UserContext } from '../data';
import { getIsLoadingCurricula, useCurricula } from '../data/curriculum-context';
import { useAppStyles } from '../styles';

// Authoring page.
export default injectIntl(({ intl }: IntlProps) => {
    const appStyles = useAppStyles();
    const [userStore, userDispatch] = useContext(UserContext);
    const isSignedIn = getIsSignedIn(userStore);
    const [store, dispatch] = useCurricula();
    const isLoading = getIsLoadingCurricula(store);
    const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
    const [deleteTarget, setDeleteTarget] = React.useState<string | undefined>(undefined);
    const [unpublishTarget, setUnpublishTarget] = React.useState<string | undefined>(undefined);

    const [linkGithubDialogOpen, setLinkGithubDialogOpen] = React.useState(false);
    useEffect(() => {
        if (store.lastError?.code === ErrorCode.MissingGitHubAccess) {
            setLinkGithubDialogOpen(true);
        }
    }, [store.lastError?.code]);

    const onCreateNewActivity = useCallback(() => {
        if (userStore.isGithubLinked !== true) {
            setLinkGithubDialogOpen(true);
        } else {
            setCreateDialogOpen(true);
        }
    }, [userStore.isGithubLinked]);

    return (
        <TrackContext.Provider value={{ container: 'authoring' }}>
            <PageContainer className={appStyles.pageContainer}>
                <div className={mergeClasses(appStyles.pageContent, 'content-width')}>
                    {!isSignedIn && <NotSignedInState />}
                    {isSignedIn && !isLoading && (
                        <div className={mergeClasses(appStyles.section)}>
                            <div className={appStyles.flexRowCentered}>
                                <h1>
                                    <FormattedMessage
                                        description="Authoring page section title"
                                        defaultMessage="Authoring"
                                    />
                                </h1>
                                <div className={appStyles.flexGrow1} />
                                {store.items.length > 0 && (
                                    <>
                                        <TrackedButton
                                            trackAction="new-activity"
                                            className={mergeClasses(appStyles.mobileHidden, appStyles.buttonPrimary)}
                                            appearance="primary"
                                            size="large"
                                            onClick={onCreateNewActivity}
                                        >
                                            <FormattedMessage
                                                description="Create new activity button title"
                                                defaultMessage="Create new activity"
                                            />
                                        </TrackedButton>
                                        <TrackedButton
                                            trackAction="new-activity"
                                            className={appStyles.mobileOnly}
                                            appearance="primary"
                                            size="medium"
                                            shape="circular"
                                            icon={<Add20Regular />}
                                            onClick={onCreateNewActivity}
                                            title={intl.formatMessage({
                                                description: 'Tooltip for create activity button',
                                                defaultMessage: 'Create new activity',
                                            })}
                                        />
                                    </>
                                )}
                            </div>
                            <div className={mergeClasses(appStyles.sectionContent, appStyles.flexGrow1)}>
                                <CurriculumList
                                    items={store.items}
                                    onCreate={onCreateNewActivity}
                                    onPublish={(curriculumId) => dispatch({ type: 'Publish', curriculumId })}
                                    onUnpublish={(curriculumId) => setUnpublishTarget(curriculumId)}
                                    onDelete={(curriculumId) => setDeleteTarget(curriculumId)}
                                />
                            </div>
                        </div>
                    )}
                </div>
                {createDialogOpen && (
                    <CreateActivityDialog
                        defaultOpen
                        onOpenChange={(_, { open }) => setCreateDialogOpen(open)}
                        onCreate={(props) => dispatch({ type: 'Create', curriculum: props })}
                    />
                )}
                {unpublishTarget !== undefined && (
                    <UnpublishActivityDialog
                        defaultOpen
                        onOpenChange={(_, { open }) => setUnpublishTarget(open ? unpublishTarget : undefined)}
                        title={store.items.find((c) => c.curriculumId === unpublishTarget)?.title ?? ''}
                        onUnpublish={() => dispatch({ type: 'Unpublish', curriculumId: unpublishTarget })}
                    />
                )}
                {deleteTarget !== undefined && (
                    <DeleteActivityDialog
                        defaultOpen
                        onOpenChange={(_, { open }) => setDeleteTarget(open ? deleteTarget : undefined)}
                        title={store.items.find((c) => c.curriculumId === deleteTarget)?.title ?? ''}
                        repoName={store.items.find((c) => c.curriculumId === deleteTarget)?.repoName}
                        onDelete={() => dispatch({ type: 'Delete', curriculumId: deleteTarget })}
                    />
                )}
                {linkGithubDialogOpen && (
                    <LinkGithubDialog
                        defaultOpen
                        onOpenChange={(_, { open }) => setLinkGithubDialogOpen(open)}
                        onConfirm={() => userDispatch({ type: 'LinkGithubAccount' })}
                        onClose={() => dispatch({ type: 'Reload' })}
                    />
                )}
            </PageContainer>
        </TrackContext.Provider>
    );
});
